import { filter, first, map, upperFirst } from "lodash";
import { Person } from "@/features/people/graphql/generated";
import { DimensionWithScores } from "@/features/people";
import { TFunction } from "i18next";
import { DimensionInsightParams } from "./Insight.types";
import { ValuesInsight } from "./useValuesInsights";
import { DimensionContext } from "@/types/data";

/**
 * Helper to get context name for translations
 */
function getContextName(
  context: string,
  person: Person | undefined,
  t: TFunction
): string {
  if (person?.first_name) return person.first_name;
  return context === "team"
    ? t("competency_insights.context_team")
    : t("competency_insights.context_they");
}

/**
 * Helper to get people associated with a dimension for a specific person ID
 */
function getInsightPerson(
  dimension: DimensionWithScores,
  defaultPersonId?: number
): Person[] {
  if (!defaultPersonId) return [];

  return map(
    filter(dimension.peopleScores, (ps) => ps.personId === defaultPersonId),
    (ps) => ps.person as Person
  );
}

const ValuesInsightRanges = {
  HIGH: 70,
  LOW: 30,
};

export const getHighligthighContext = (
  context: DimensionContext,
  person: Person | undefined,
  t: TFunction
): string => {
  if (context === "team" && !person) return t("context_label.team");
  if (context === "user") return t("context_label.user");
  return person?.first_name || getContextName(context, person, t);
};

/**
 * Creates a top values insight for a single dimension (score >= 70)
 * Returns null if dimension doesn't qualify for a top values insight
 */
export function getTopValuesInsight(
  dimension: DimensionWithScores,
  params: DimensionInsightParams,
  t: TFunction
): ValuesInsight | null {
  const score = dimension.score?.value || 0;

  if (score < ValuesInsightRanges.HIGH) {
    return null;
  }

  const { context, person, defaultPersonId } = params;
  const members = getInsightPerson(dimension, defaultPersonId);

  const highlightContext = getHighligthighContext(
    context,
    person || first(members),
    t
  );

  return {
    dimension,
    type: "important_values",
    title: dimension.title,
    subtitle: dimension.description,
    description: dimension.description,
    people: members,
    tags: [
      {
        label: t("values_insights.top_motivator"),
        color: "text-green-500",
        icon: "Compass",
        bg: "bg-green-500",
      },
    ],
    highlight: {
      color: "bg-green-100",
      title: t("values_insights.top_motivator_highlight_title"),
      description: upperFirst(
        t("values_insights.top_motivator_highlight_description", {
          context: highlightContext,
        })
      ),
      tag: {
        label: t("values_insights.top_motivator"),
        color: "text-green-500",
        icon: "Compass",
        bg: "bg-green-500",
      },
    },
  };
}

/**
 * Creates a bottom values insight for a single dimension (score <= 30)
 * Returns null if dimension doesn't qualify for a bottom values insight
 */
export function getBottomValuesInsight(
  dimension: DimensionWithScores,
  params: DimensionInsightParams,
  t: TFunction
): ValuesInsight | null {
  const score = dimension.score?.value || 0;

  if (score > ValuesInsightRanges.LOW) {
    return null;
  }

  const { context, person, defaultPersonId } = params;
  const members = getInsightPerson(dimension, defaultPersonId);

  const highlightContext = getHighligthighContext(
    context,
    person || first(members),
    t
  );

  return {
    dimension,
    type: "least_important_values",
    title: dimension.title,
    subtitle: dimension.description,
    description: dimension.description,
    people: members,
    tags: [
      {
        label: t("values_insights.low_priority"),
        color: "text-orange-500",
        icon: "ArrowDown",
        bg: "bg-orange-500",
      },
    ],
    highlight: {
      color: "bg-orange-100",
      title: t("values_insights.low_priority_highlight_title"),
      description: upperFirst(
        t("values_insights.low_priority_highlight_description", {
          context: highlightContext,
        })
      ),
      tag: {
        label: t("values_insights.low_priority"),
        color: "text-orange-500",
        icon: "ArrowDown",
        bg: "bg-orange-500",
      },
    },
  };
}

/**
 * Returns a single insight for a values dimension
 * Automatically determines whether to return top or bottom values insight
 * Returns null if dimension doesn't qualify for any insight (score 31-69)
 */
export function getValuesInsight(
  dimension: DimensionWithScores,
  params: DimensionInsightParams,
  t: TFunction
): ValuesInsight | null {
  return (
    getBottomValuesInsight(dimension, params, t) ||
    getTopValuesInsight(dimension, params, t)
  );
}
